import { useEffect, useMemo } from "react";
import {
  ActionBanner,
  DialogIcon,
  EmptyState,
  illustrations,
  MoonCheckboxBox,
  Row,
  ListRowSkeleton,
} from "@louise/moon-ui";
import { View, Text } from "react-native";
import { MapPin } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { parseDateOrTimestamp } from "@src/packages/utils/date";
import { useFrozenSpecimens, type Specimen } from "./useSpecimens";
import {
  canConfirmFrozenSpecimenSelection,
  getSelectionSourceCycleId,
} from "./frozenSpecimenAvailability";
import { PHYSICALLY_CONSUMED_SPECIMEN_USAGES } from "./frozenSpecimenUsages";
import frozenEggsIllustration from "@src/packages/homepage/assets/images/outcome-funnel/frozen-eggs.png";
import { semanticColors } from "@louise/theme/tokens";

/**
 * The subset of Specimen fields the picker actually reads. Widening the
 * `overrideSpecimens` prop from `Specimen[]` to this narrower shape lets the
 * CryoVault reconstruct picker entries from a real oocyte cohort's members
 * (which are not full Specimen records) without a cast.
 */
export type PickerSpecimen = Pick<
  Specimen,
  | "id"
  | "type"
  | "status"
  | "collectedAt"
  | "cycleId"
  | "notes"
  | "storageLocations"
  | "specimenUsages"
> & {
  isDonor?: Specimen["isDonor"];
};

export type FrozenSpecimenSelection = {
  specimenIds: string[];
  sourceCycleId: string | null;
};

type Props = {
  type: "oocyte" | "embryo";
  selectedIds: string[];
  onChange: (selection: FrozenSpecimenSelection) => void;
  /** Lets the dialog reveal confirmation only for a loaded, eligible selection. */
  onCanConfirmChange?: (canConfirm: boolean) => void;
  /**
   * When present, restrict the picker to these specimen IDs. Used by the
   * CryoVault's virtual-cohort tile to open a picker scoped to one retrieval
   * batch (same cycle + freeze date + facility) instead of the whole vault.
   */
  scopedSpecimenIds?: readonly string[];
  /**
   * When present, use this list as the source instead of `useFrozenSpecimens`.
   * A real oocyte cohort's members are not addressable through the specimens
   * query — they are stored as a cohort record. The CryoVault reconstructs
   * `PickerSpecimen` shapes from the cohort's members so the picker can
   * render them.
   */
  overrideSpecimens?: readonly PickerSpecimen[];
};

const TYPE_LABEL: Record<Props["type"], { singular: string; plural: string }> =
  {
    oocyte: { singular: "egg", plural: "eggs" },
    embryo: { singular: "embryo", plural: "embryos" },
  };

function activeFacility(specimen: PickerSpecimen): string {
  return (
    specimen.storageLocations.find(
      (l) => l.isActive && l.storageType === "frozen",
    )?.facility ?? "Unknown facility"
  );
}

function formatCollectedDate(iso: string): string {
  try {
    const d = parseDateOrTimestamp(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function FrozenSpecimenSelector({
  type,
  selectedIds,
  onChange,
  onCanConfirmChange,
  scopedSpecimenIds,
  overrideSpecimens,
}: Props) {
  const t = useTranslations();
  const { specimens: fetchedSpecimens, isLoading } = useFrozenSpecimens();
  const specimens = overrideSpecimens ?? fetchedSpecimens;
  const sourceIsLoading = overrideSpecimens === undefined && isLoading;
  const labels = TYPE_LABEL[type];

  const filtered = useMemo(() => {
    const byType = specimens.filter((s) => s.type === type);
    if (!scopedSpecimenIds) return byType;
    const allowed = new Set(scopedSpecimenIds);
    return byType.filter((s) => allowed.has(s.id));
  }, [specimens, type, scopedSpecimenIds]);

  const byFacility = useMemo(() => {
    const groups = new Map<string, PickerSpecimen[]>();
    for (const s of filtered) {
      const facility = activeFacility(s);
      const list = groups.get(facility) ?? [];
      list.push(s);
      groups.set(facility, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  const selectedSourceCycle = useMemo(() => {
    return getSelectionSourceCycleId(filtered, selectedIds);
  }, [selectedIds, filtered]);

  const physicallyConsumedIds = useMemo(
    () =>
      new Set(
        filtered
          .filter((specimen) =>
            (specimen.specimenUsages ?? []).some((usage) =>
              PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
            ),
          )
          .map((specimen) => specimen.id),
      ),
    [filtered],
  );

  // Cohort detail rows retain every member for history. Keep the pending
  // selection in sync with the visible, unused rows before it can be
  // submitted.
  useEffect(() => {
    const availableIds = new Set(
      filtered
        .filter((specimen) => !physicallyConsumedIds.has(specimen.id))
        .map((specimen) => specimen.id),
    );
    onCanConfirmChange?.(
      canConfirmFrozenSpecimenSelection(
        selectedIds,
        availableIds,
        sourceIsLoading,
      ),
    );
    if (sourceIsLoading) return;
    const selectedAvailableIds = selectedIds.filter((id) =>
      availableIds.has(id),
    );
    if (selectedAvailableIds.length === selectedIds.length) return;
    onChange({
      specimenIds: selectedAvailableIds,
      sourceCycleId: getSelectionSourceCycleId(filtered, selectedAvailableIds),
    });
  }, [
    filtered,
    onCanConfirmChange,
    onChange,
    physicallyConsumedIds,
    selectedIds,
    sourceIsLoading,
  ]);

  const handleToggle = (specimen: PickerSpecimen) => {
    const isSelected = selectedIds.includes(specimen.id);
    let newIds: string[];
    if (isSelected) {
      newIds = selectedIds.filter((id) => id !== specimen.id);
    } else {
      newIds = [...selectedIds, specimen.id];
    }
    const sourceCycleId =
      newIds.length > 0
        ? (filtered.find((s) => s.id === newIds[0])?.cycleId ?? null)
        : null;
    onChange({ specimenIds: newIds, sourceCycleId });
  };

  if (sourceIsLoading) {
    return (
      <View testID="frozen-specimen-selector-loading">
        {[0, 1].map((row) => (
          <ListRowSkeleton key={row} showLeading showSubtitle />
        ))}
      </View>
    );
  }

  if (filtered.length === 0) {
    return (
      <EmptyState
        testID="frozen-specimen-picker-empty-state"
        density="compact"
        titleVariant="featured"
        style={{ paddingBottom: 0 }}
        illustration={
          // Trim each asset's transparent gutters (wider on the blastocyst)
          // so the visible artwork stays centered in the same 96px frame
          // and preserves the shared illustration-to-title gap.
          <View style={{ width: 96, height: 96, overflow: "hidden" }}>
            <DialogIcon
              source={
                type === "oocyte"
                  ? frozenEggsIllustration
                  : illustrations.blastocyst
              }
              width={type === "oocyte" ? 124 : 146}
              height={type === "oocyte" ? 124 : 146}
              style={{
                position: "absolute",
                left: type === "oocyte" ? -14 : -25,
                top: type === "oocyte" ? -14 : -25,
              }}
            />
          </View>
        }
        title={
          scopedSpecimenIds !== undefined
            ? t("care.select_specimens.no_available_cohort_eggs")
            : t(
                type === "oocyte"
                  ? "care.select_specimens.no_frozen_eggs_yet"
                  : "care.select_specimens.no_frozen_embryos_yet",
              )
        }
        subtitle={t(
          scopedSpecimenIds !== undefined
            ? "care.select_specimens.only_available_frozen_eggs_can_be_selected"
            : "care.select_specimens.specimens_appear_once_added_to_vault",
        )}
      />
    );
  }

  const showFewEggsWarning =
    type === "oocyte" && selectedIds.length > 0 && selectedIds.length < 4;

  return (
    <View style={{ gap: 14 }}>
      {byFacility.map(([facility, items]) => (
        <View key={facility} style={{ gap: 6 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              marginBottom: 2,
            }}
          >
            <MapPin
              size={12}
              color={semanticColors.text.faint}
              strokeWidth={1.5}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: semanticColors.text.secondary,
              }}
            >
              {facility}
            </Text>
          </View>
          {items.map((specimen, idx) => {
            const selected = selectedIds.includes(specimen.id);
            const blockedByCycle =
              !selected &&
              selectedSourceCycle !== null &&
              specimen.cycleId !== selectedSourceCycle;
            const blockedByUse = physicallyConsumedIds.has(specimen.id);
            const blocked = blockedByCycle || blockedByUse;
            const specimenLabel = `${labels.singular.charAt(0).toUpperCase()}${labels.singular.slice(1)} #${idx + 1}`;
            // The canonical Row with the shared checkbox. The whole row is
            // the control, so the checkbox is the box alone. Input fill: the
            // picker sits in a dialog's scroll area, which clips a glass
            // row's shadow.
            return (
              <Row
                key={specimen.id}
                surface="input"
                // Dense keeps a batch of eggs in view above the buttons.
                density="dense"
                paddingVertical={10}
                leading={<MoonCheckboxBox checked={selected} />}
                leadingSize={24}
                title={specimenLabel}
                titleRight={
                  specimen.isDonor === true ? (
                    <View
                      style={{
                        backgroundColor: semanticColors.accent.tint,
                        paddingHorizontal: 6,
                        paddingVertical: 1,
                        borderRadius: 4,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 9,
                          fontWeight: "600",
                          color: semanticColors.accent.default,
                          letterSpacing: 0.4,
                        }}
                      >
                        DONOR
                      </Text>
                    </View>
                  ) : undefined
                }
                subtitle={`Collected ${formatCollectedDate(specimen.collectedAt)}${
                  blockedByUse
                    ? ` · ${t("care.select_specimens.already_recorded_as_used")}`
                    : blockedByCycle
                      ? ` · ${t("care.select_specimens.different_retrieval")}`
                      : ""
                }`}
                chevron={false}
                disabled={blocked}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: selected, disabled: blocked }}
                action="specimen_row"
                onPress={() => handleToggle(specimen)}
              />
            );
          })}
        </View>
      ))}
      {showFewEggsWarning && (
        <ActionBanner
          variant="warning"
          message={t("care.select_specimens.few_eggs_recommendation")}
        />
      )}
    </View>
  );
}
